"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command"
import { ChevronsUpDown, Check } from "lucide-react"

export interface ComboboxOption {
  value: string
  label: string
}

export interface EditableComboboxProps {
  id?: string
  value: string
  onValueChange: (value: string) => void
  options: ComboboxOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  contentClassName?: string
  itemClassName?: string
  footer?: React.ReactNode
}

export function EditableCombobox({
  id,
  value,
  onValueChange,
  options,
  placeholder = "",
  disabled,
  className,
  contentClassName,
  itemClassName,
  footer,
}: EditableComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(
    options.find(o => o.value === value)?.label || ""
  )
  const [isValid, setIsValid] = React.useState(true)

  // Sync internal inputValue when controlled value prop changes
  React.useEffect(() => {
    const label = options.find(o => o.value === value)?.label || ""
    setInputValue(label)
  }, [value, options])

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(inputValue.toLowerCase())
  )

  // Validate input whenever it changes
  React.useEffect(() => {
    const matchesOption = options.some(
      opt => opt.label.toLowerCase() === inputValue.toLowerCase()
    )
    setIsValid(matchesOption || inputValue === "")
  }, [inputValue, options])

  const handleOpenChange = (isOpen: boolean) => {
    // If closing and input is invalid, reset to last valid value
    if (!isOpen && !isValid) {
      const lastValidOption = options.find(o => o.value === value)
      setInputValue(lastValidOption?.label || "")
      setIsValid(true)
    }
    setOpen(isOpen)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex bg-background w-full lg:w-fit lg:min-w-[180px] justify-start gap-2",
            "text-base pl-0 pr-8 py-2 h-auto min-h-[40px] rounded-none",
            "focus-visible:outline-none",
            "border-0 border-b border-transparent",
            !inputValue && "text-muted-foreground",
            !isValid && "border-destructive text-destructive",
            "disabled:opacity-100",
            "data-[disabled]:[&_svg]:hidden",
            open && "border-primary",
            "hover:bg-background",
            className
          )}
          variant="ghost"
          onClick={() => setOpen(true)}
        >
          <span className={cn("truncate", !value && "text-gray-400")}>
            {inputValue || placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 ml-auto" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0 bg-background", contentClassName)}>
        <Command className="bg-background">
          <CommandInput
            placeholder={`Search ${placeholder.toLowerCase()}…`}
            value={inputValue}
            onValueChange={(val) => {
              setInputValue(val)
            }}
            className={cn(
              "border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-background",
              !isValid && "text-destructive"
            )}
            autoFocus
          />
          <CommandList className="bg-background">
            <CommandEmpty>
              {inputValue ? "No matching options." : "No options available."}
            </CommandEmpty>
            <CommandGroup className="bg-background">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={(val) => {
                    onValueChange(val)
                    setOpen(false)
                    const sel = options.find(o => o.value === val)
                    setInputValue(sel?.label || "")
                    setIsValid(true)
                  }}
                  className={cn("flex items-center gap-2 bg-background hover:bg-accent", itemClassName)}
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === opt.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {footer && (
            <div className="border-t bg-background p-2">
              {footer}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
} 